// overlayUtils.js
// Utility functions for overlay construction and chart data access

// --- Data Access Helpers ---

// 2-letter planet abbreviations for overlay labels
export const PLANET_ABBREVIATIONS = {
  Sun: 'Su',
  Moon: 'Mo',
  Mercury: 'Me',
  Venus: 'Ve',
  Mars: 'Ma',
  Jupiter: 'Ju',
  Saturn: 'Sa',
  Uranus: 'Ur',
  Neptune: 'Ne',
  Pluto: 'Pl',
  Rahu: 'Ra',
  Ketu: 'Ke',
};


export function findDateIndex(dateToFind, allDates, direction = 'forward') {
  if (!dateToFind || !Array.isArray(allDates) || allDates.length === 0) {
    return -1;
  }

  const targetDateStr = dateToFind.split('T')[0];
  const exactMatchIndex = allDates.findIndex(d => d.split('T')[0] === targetDateStr);
  if (exactMatchIndex !== -1) {
    return exactMatchIndex;
  }

  const sortedDates = [...allDates].sort((a, b) => new Date(a) - new Date(b));

  if (direction === 'forward') {
    const nextDate = sortedDates.find(d => d.split('T')[0] >= targetDateStr);
    return nextDate ? allDates.indexOf(nextDate) : -1;
  } else { // backward
    const prevDate = sortedDates.filter(d => d.split('T')[0] <= targetDateStr).pop();
    return prevDate ? allDates.indexOf(prevDate) : -1;
  }
}

export function getClose(date, barData, direction = 'forward') {
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) {
    console.warn('[getClose] Invalid barData:', barData);
    return undefined;
  }
  const idx = findDateIndex(date, barData.dates, direction);
  if (idx === -1) {
    console.warn(`[getClose] No matching index for date '${date}' (direction: ${direction}). Available dates:`, barData.dates);
    return undefined;
  }
  if (!barData.ohlc[idx] || typeof barData.ohlc[idx][3] !== 'number') {
    console.warn(`[getClose] No valid OHLC at index ${idx} for date '${date}'. OHLC:`, barData.ohlc[idx]);
    return undefined;
  }
  console.log(`[getClose] Found close for date '${date}' (idx: ${idx}):`, barData.ohlc[idx][3]);
  return barData.ohlc[idx][3]; // [open, high, low, close]
}

// --- Single Overlay Creation Utilities ---

export const createVLineOverlay = (date, label, color = 'black', props = {}) => {
  const allDates = props.extendedDates || [];
  const formattedDate = date ? date.split('T')[0] : '';
  // Use the date as-is if present in extendedDates or barData.dates
  const finalDate = (allDates.includes(formattedDate) || allDates.includes(date)) ? formattedDate : formattedDate;
  return {
    type: 'vLine',
    date: finalDate,
    label,
    color,
    ...props,
  };
};

export const createAreaHighlightOverlay = (date1, date2, label, color, props = {}) => {
  const allDates = props.extendedDates || [];
  // Use the date as-is if present in extendedDates or barData.dates
  const d1 = (allDates.includes(date1) || allDates.includes(date1?.split('T')[0])) ? date1 : date1;
  const d2 = (allDates.includes(date2) || allDates.includes(date2?.split('T')[0])) ? date2 : date2;
  return {
    type: 'areaHighlight',
    date1: d1,
    date2: d2,
    label,
    color,
    ...props,
  };
};

import { snapToNearestChartDate } from './dateUtils';
export const createTrendLineOverlay = (date1, date2, barData, props = {}) => {
  // Optionally pass realBarEndDate (YYYY-MM-DD) in props
  const realBarEndDate = props.realBarEndDate;
  const chartDates = barData?.dates || [];

  const normDate1 = (date1 && chartDates.includes(date1)) ? date1 :
    (chartDates.includes(date1?.split('T')[0]) ? date1?.split('T')[0] :
      ((realBarEndDate && date1.split('T')[0] > realBarEndDate) ? date1 : snapToNearestChartDate(date1, chartDates)));
  const normDate2 = (date2 && chartDates.includes(date2)) ? date2 :
    (chartDates.includes(date2?.split('T')[0]) ? date2?.split('T')[0] :
      ((realBarEndDate && date2.split('T')[0] > realBarEndDate) ? date2 : snapToNearestChartDate(date2, chartDates)));


  // Use high-to-high logic for trendline direction and overlay values
  const idx1 = barData && Array.isArray(barData.dates) && barData.dates.length > 0 && normDate1 ? barData.dates.findIndex(d => d.split('T')[0] === normDate1.split('T')[0]) : -1;
  const idx2 = barData && Array.isArray(barData.dates) && barData.dates.length > 0 && normDate2 ? barData.dates.findIndex(d => d.split('T')[0] === normDate2.split('T')[0]) : -1;
  const high1 = idx1 !== -1 && barData.ohlc && Array.isArray(barData.ohlc) && barData.ohlc.length > idx1 && barData.ohlc[idx1] ? barData.ohlc[idx1][1] : undefined;
  const high2 = idx2 !== -1 && barData.ohlc && Array.isArray(barData.ohlc) && barData.ohlc.length > idx2 && barData.ohlc[idx2] ? barData.ohlc[idx2][1] : undefined;

  if (typeof high1 !== 'number' || typeof high2 !== 'number') {
    return null;
  }

  let trendlineColor;
  if (high2 > high1) {
    trendlineColor = '#1b5e20'; // dark green
  } else if (high2 < high1) {
    trendlineColor = '#b71c1c'; // dark red
  } else {
    trendlineColor = 'gray';
  }

  if (normDate1 === '2025-08-08' || normDate2 === '2025-08-08') {
    console.warn('[DEBUG][createTrendLineOverlay] normDate1 or normDate2 is 2025-08-08!', {date1, date2, normDate1, normDate2, barData, props});
  }

  return {
    type: 'trendLine',
    date1: normDate1,
    date2: normDate2,
    price1: high1,
    price2: high2,
    color: trendlineColor,
    label: props.label,
    ...props,
  };
}

// --- Composite Overlay Creation Utilities ---

export function createVLineOverlays(events, studyLabelPrefix) {
  return events
    .map(event => {
      if (!event || !event.date) return null;
      const toLocation = event.toSign || event.toNakshatra || '';
      const formattedDate = event.date.split('T')[0];
      const label = toLocation ? `${studyLabelPrefix} -> ${toLocation}` : `${studyLabelPrefix} on ${formattedDate}`;
      return createVLineOverlay(event.date, label);
    })
    .filter(Boolean);
}

export function createOverlaysForDatePair(pair, color, studyLabel, barData, props = {}) {
  // Always use yyyy-MM-dd for all overlay dates
  // Helper to get full ISO date from barData.dates
  function getFullDateString(date, barDates) {
    if (!date || !barDates) return date;
    // Try to find exact match first
    if (barDates.includes(date)) return date;
    // Try to find match by YYYY-MM-DD
    const found = barDates.find(d => d.startsWith(date));
    return found || date;
  }
  let rawDate1 = (Array.isArray(pair) ? pair[0] : pair.date1 || pair.startDate);
  let rawDate2 = (Array.isArray(pair) ? pair[1] : pair.date2 || pair.endDate);
  let shortDate1 = rawDate1?.split('T')[0];
  let shortDate2 = rawDate2?.split('T')[0];
  const date1 = getFullDateString(shortDate1, barData && barData.dates);
  const date2 = getFullDateString(shortDate2, barData && barData.dates);
  if (date1 === '2025-08-08' || date2 === '2025-08-08') {
    console.warn('[DEBUG][createOverlaysForDatePair] date1 or date2 is 2025-08-08!', {pair, color, studyLabel, barData, props});
  }
  if (barData && barData.dates) {
    console.log('[OverlayDebug] Creating overlay for', date1, date2, 'in extendedBarData.dates?', barData.dates.includes(date1), barData.dates.includes(date2), 'dates.length:', barData.dates.length);
  }

  if (!date1 || !date2) {
    console.warn('Skipping overlay creation for invalid pair:', pair);
    return [];
  }

  const sharedProps = {
    planet1: props.planet1,
    planet2: props.planet2 !== undefined ? props.planet2 : null,
    sign: props.sign,
    nakshatra: props.nakshatra !== undefined ? props.nakshatra : null,
    study: studyLabel
  };

    // Compose the label as '[planet] in [sign] from yyyy-MM-dd to yyyy-MM-dd' for degree range study
  // For AspectsAreaHighlightDialog overlays, always use the format: [planet1 abbr]-fromDeg-toDeg-[planet2 abbr]-date
  let overlayLabel;
  if (props.planet1 && props.planet2 && props.fromDegree !== undefined && props.toDegree !== undefined) {
    // Use strict label format for AspectsAreaHighlightDialog overlays
    const abbr1 = (props.planet1 || '').slice(0,2).toUpperCase();
    const abbr2 = (props.planet2 || '').slice(0,2).toUpperCase();
    const deg1 = props.fromDegree;
    const deg2 = props.toDegree;
    const dateStr = (date1 || '').split('T')[0];
    overlayLabel = `${abbr1}-${deg1}-${deg2}-${abbr2}-${dateStr}`;
  } else if (props.nakshatra) {
    overlayLabel = `${props.planet1 || props.planet || '[planet]'} in ${props.nakshatra || '[nakshatra]'}${props.charan ? '-' + props.charan : ''} - ${(date1 || '').split('T')[0]}`;
  } else if (props.sign) {
    overlayLabel = `${props.planet1 || ''} in ${props.sign || ''} ${(date1 || '').split('T')[0]}`;
  } else {
    overlayLabel = '';
  }
  console.log('[createOverlaysForDatePair] Final overlay label:', overlayLabel, 'props:', props);

  // Mirror the default label offset: move label same distance to right of line as it was to left (-18 => +18)
  const vLineProps = { ...sharedProps, labelOffset: 18 };

  // Calculate the center date between date1 and date2 for label placement (trendline midpoint)
  let labelX = undefined;
  // Use extendedBarData.dates if available for labelX calculation
  const allDates = (props.extendedDates || (barData && barData.dates)) || [];
  if (Array.isArray(allDates)) {
    const idx1 = allDates.findIndex(d => d.split('T')[0] === (date1 || '').split('T')[0]);
    const idx2 = allDates.findIndex(d => d.split('T')[0] === (date2 || '').split('T')[0]);
    if (idx1 !== -1 && idx2 !== -1 && idx2 > idx1) {
      const midIdx = Math.floor((idx1 + idx2) / 2);
      labelX = allDates[midIdx];
    }
  }

  // Use user-selected color exactly as provided for area highlights
  let highlightColor = color;

  // Area highlight overlay: no label (visual only)
  const areaHighlight = createAreaHighlightOverlay(date1, date2, '', highlightColor, { ...sharedProps, labelX });
  // Place the vLineStart label at the center of the highlight (trendline midpoint)
  const vLineStartProps = { ...sharedProps, labelX };

  // Vertical lines: left/start gets the special label (if non-empty), right/end is blank
  const vLineStartLabel = overlayLabel && overlayLabel.trim() ? overlayLabel : 'DEBUG-NOLABEL';
  const vLineStart = createVLineOverlay(date1, vLineStartLabel, 'black', { ...vLineStartProps, customLabelColor: color }); // Label only on left, label color = highlight
  console.log('[createOverlaysForDatePair] vLineStart overlay:', vLineStart);
  const vLineEnd = createVLineOverlay(date2, '', 'black', vLineProps); // No label, no icon on right
  // Trend line overlay with label (for debugging/hover, not chart)
  // Always use extendedBarData (passed as barData param) for trendLine overlays
  const trendLine = createTrendLineOverlay(date1, date2, props.extendedBarData || barData, { ...sharedProps, label: overlayLabel });
  if (!trendLine) {
    console.warn('[createOverlaysForDatePair] TrendLine overlay not created: price1 or price2 missing', {
      date1, date2, overlayLabel, barData: props.extendedBarData || barData, sharedProps
    });
  } else {
    console.log('[createOverlaysForDatePair] TrendLine overlay created:', trendLine);
  }

  // Ensure all overlays have required fields
  const overlays = [areaHighlight, vLineStart, vLineEnd, trendLine].map(overlay => {
    if (!overlay) return null;
    return {
      label: overlay.label !== undefined ? overlay.label : overlayLabel,
      planet1: overlay.planet1,
      planet2: overlay.planet2 !== undefined ? overlay.planet2 : null,
      sign: overlay.sign !== undefined ? overlay.sign : null,
      nakshatra: overlay.nakshatra !== undefined ? overlay.nakshatra : null,
      labelX: overlay.labelX !== undefined ? overlay.labelX : null,
      ...overlay
    };
  });

  return overlays.filter(Boolean);
}
